import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import AdminProducts from '../components/admin/AdminProducts';
import AdminCategories from '../components/admin/AdminCategories';
import AdminSEO from '../components/admin/AdminSEO';
import AdminDashboard from '../components/admin/AdminDashboard';
import AdminOrders from '../components/admin/AdminOrders';
import AdminCustomers from '../components/admin/AdminCustomers';
import AdminDiscounts from '../components/admin/AdminDiscounts';
import AdminCampaignCodes from '../components/admin/AdminCampaignCodes';
import MarketingPages from '../components/admin/MarketingPages';
import FloristBlog from '../components/admin/FloristBlog';
import Visitors from './admin/Visitors';
import AdminAccounting from '../components/admin/AdminAccounting';

const AdminWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f5f7;
`;

const Sidebar = styled.aside<{ $mobileOpen: boolean }>`
  width: 260px;
  background-color: var(--color-secondary);
  color: white;
  padding: var(--spacing-6);
  box-shadow: var(--shadow-lg);
  
  @media (max-width: 768px) {
    width: ${props => props.$mobileOpen ? '260px' : '0'};
    overflow: hidden;
    transition: width 0.3s ease;
    position: fixed;
    height: 100vh;
    z-index: 1000;
  }
`;

const SidebarHeader = styled.div`
  margin-bottom: var(--spacing-8);
  
  h1 {
    font-size: var(--font-size-xl);
    font-weight: 600;
    margin-bottom: var(--spacing-2);
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: var(--font-size-sm);
  }
`;

const SidebarNav = styled.nav`
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  
  li {
    margin-bottom: var(--spacing-2);
  }
`;

const NavLink = styled(Link)<{ active?: boolean }>`
  display: flex;
  align-items: center;
  padding: var(--spacing-3) var(--spacing-4);
  color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.8)'};
  background-color: ${props => props.active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border-radius: var(--border-radius);
  text-decoration: none;
  transition: all 0.2s ease;
  font-weight: ${props => props.active ? '500' : '400'};
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
  
  .icon {
    margin-right: var(--spacing-3);
    font-size: var(--font-size-lg);
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: var(--spacing-6);
  
  @media (max-width: 768px) {
    padding: var(--spacing-4);
  }
`;

const Header = styled.header`
  background: white;
  padding: var(--spacing-4) var(--spacing-6);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  margin-bottom: var(--spacing-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  h1 {
    font-size: var(--font-size-2xl);
    font-weight: 600;
    color: var(--color-secondary);
    margin: 0;
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  background: var(--color-secondary);
  color: white;
  border: none;
  padding: var(--spacing-2);
  border-radius: var(--border-radius);
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  
  .avatar {
    width: 36px;
    height: 36px;
    background-color: var(--color-primary);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
  }
  
  .info {
    .name {
      font-weight: 500;
      color: var(--color-secondary);
    }
    
    .role {
      font-size: var(--font-size-sm);
      color: var(--color-text-light);
    }
  }
`;

const LogoutButton = styled.button`
  background: none;
  border: 1px solid var(--color-border);
  color: var(--color-text);
  padding: var(--spacing-2) var(--spacing-3);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  cursor: pointer;
  margin-left: var(--spacing-3);
  
  &:hover {
    background-color: #fee2e2;
    border-color: #fecaca;
    color: #dc2626;
  }
`;

const Admin: React.FC = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    if (window.confirm('Är du säker på att du vill logga ut?')) {
      logout();
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/admin/produkter')) return 'Produkthantering';
    if (path.includes('/admin/kategorier')) return 'Kategorihantering';
    if (path.includes('/admin/ordrar')) return 'Orderhantering';
    if (path.includes('/admin/rabatter')) return 'Rabatter & Kampanjer';
    if (path.includes('/admin/kampanjkoder')) return 'Kampanjkoder';
    if (path.includes('/admin/seo')) return 'SEO-inställningar';
    if (path.includes('/admin/marknadsforingssidor')) return 'Marknadsföringssidor';
    if (path.includes('/admin/floristblogg')) return 'Floristbloggen';
    if (path.includes('/admin/kundregister')) return 'Kundregister';
    if (path.includes('/admin/besokare')) return 'Besökarstatistik';
    if (path.includes('/admin/bokforing')) return 'Bokföring & kvitton';
    return 'Administratörspanel';
  };

  const navigation = [
    { path: '/admin', label: 'Dashboard', icon: '📊' },
    { path: '/admin/produkter', label: 'Produkter', icon: '📦' },
    { path: '/admin/kategorier', label: 'Kategorier', icon: '🗂️' },
    { path: '/admin/ordrar', label: 'Ordrar', icon: '🚚' },
    { path: '/admin/rabatter', label: 'Rabatter', icon: '🏷️' },
    { path: '/admin/kampanjkoder', label: 'Kampanjkoder', icon: '🎫' },
    { path: '/admin/kundregister', label: 'Kundregister', icon: '👥' },
    { path: '/admin/seo', label: 'SEO-inställningar', icon: '🔍' },
    { path: '/admin/marknadsforingssidor', label: 'Marknadsföringssidor', icon: '📄' },
    { path: '/admin/floristblogg', label: 'Floristbloggen', icon: '📝' },
    { path: '/admin/besokare', label: 'Besökare', icon: '📈' },
    { path: '/admin/bokforing', label: 'Bokföring', icon: '📑' }
  ];

  return (
    <AdminWrapper>
      <Sidebar $mobileOpen={mobileMenuOpen}>
        <SidebarHeader>
          <h1>Lillaflora Admin</h1>
          <p>Kontrollpanel</p>
        </SidebarHeader>
        
        <SidebarNav>
          <ul>
            {navigation.map(item => (
              <li key={item.path}>
                <NavLink 
                  to={item.path} 
                  active={isActive(item.path)}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="icon">{item.icon}</span>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </SidebarNav>
      </Sidebar>
      
      <MainContent>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)' }}>
            <MobileMenuToggle onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              ☰
            </MobileMenuToggle>
            <h1>{getPageTitle()}</h1>
          </div>
          
          <UserInfo>
            <div className="avatar">{user?.username.charAt(0).toUpperCase()}</div>
            <div className="info">
              <div className="name">{user?.username}</div>
              <div className="role">Administratör</div>
            </div>
            <LogoutButton onClick={handleLogout}>
              Logga ut
            </LogoutButton>
          </UserInfo>
        </Header>
        
        <Routes>
          <Route path="/" element={<AdminDashboard />} />
          <Route path="/produkter/*" element={<AdminProducts />} />
          <Route path="/kategorier/*" element={<AdminCategories />} />
          <Route path="/ordrar/*" element={<AdminOrders />} />
          <Route path="/rabatter/*" element={<AdminDiscounts />} />
          <Route path="/kampanjkoder/*" element={<AdminCampaignCodes />} />
          <Route path="/kundregister/*" element={<AdminCustomers />} />
          <Route path="/seo/*" element={<AdminSEO />} />
          <Route path="/marknadsforingssidor/*" element={<MarketingPages />} />
          <Route path="/floristblogg" element={<FloristBlog />} />
          <Route path="/besokare/*" element={<Visitors />} />
          <Route path="/bokforing" element={<AdminAccounting />} />
        </Routes>
      </MainContent>
    </AdminWrapper>
  );
};

export default Admin;
